import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HD_MOON_TRANSIT_TYPE_COLORS } from './hd-moon-transit-card.models';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { getHdPlanetDisplayConfig } from '../../bodygraph/planet-grid/hd-planet-display-config';

/**
 * Sectie van de legende: de page plaatst twee instanties — kanaaltypes mét de
 * cyclusfeiten-zin linksonder (onder de design-grid) en de symbolen
 * rechtsonder (onder de personality-grid), smal zodat er ruimte blijft voor
 * cards (finetune-rondes 3–4).
 */
export type HdMoonTransitLegendSection = 'channels' | 'symbols';

/**
 * Legende bij het maan-transit wiel (spec
 * `2026-07-06-hd-moon-transit-chart-design.md` §6.4): kanaaltype-kleuren,
 * ring-/fixatie-/brug-/today-uitleg en de maancyclus-feiten, opgesplitst per
 * {@link HdMoonTransitLegendSection}; items staan onder elkaar.
 */
@Component({
  selector: 'hd-moon-transit-legend',
  imports: [SharedModule],
  templateUrl: './hd-moon-transit-legend.component.html',
  styleUrl: './hd-moon-transit-legend.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMoonTransitLegendComponent {
  readonly section = input<HdMoonTransitLegendSection>('channels');

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly typeSwatches = [
    { color: HD_MOON_TRANSIT_TYPE_COLORS.projected, labelKey: 'humanDesign.chart.moonTransit.legend.projected' },
    { color: HD_MOON_TRANSIT_TYPE_COLORS.generated, labelKey: 'humanDesign.chart.moonTransit.legend.generated' },
    { color: HD_MOON_TRANSIT_TYPE_COLORS.manifested, labelKey: 'humanDesign.chart.moonTransit.legend.manifested' },
  ] as const;

  /** Astronomicon maan-glyph — zelfde marker als op het wiel. */
  protected readonly moonGlyph = getHdPlanetDisplayConfig('moon').astronomiconChar;

  protected t(key: string): string {
    return this.transloco.translate(key, undefined, this.lang());
  }
}
